import { useEffect, useReducer } from 'react';
import { C, F } from './theme';

// Step 5d Phase 1 — always-honest data-age banner (design v2 §8, passive class).
// Shown ONLY while the board is rendering from the IndexedDB cache after a network-level
// fetch failure; cleared by the next successful fetch. Sized for tablet portrait
// (manifest is portrait-primary): ≥13px text, generous padding, wraps cleanly.

function formatAge(ms: number): string {
  const mins = Math.floor(ms / 60_000);
  if (mins < 1) return 'moments';
  if (mins < 60) return `${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 48) {
    const rem = mins % 60;
    return rem > 0 ? `${hours} h ${rem} min` : `${hours} h`;
  }
  return `${Math.floor(hours / 24)} days`;
}

export function OfflineDataBanner({ scopeLabel, refreshedAt }: { scopeLabel: string; refreshedAt: string }) {
  // Re-render each minute so the displayed age stays honest while the outage lasts.
  const [, tick] = useReducer((c: number) => c + 1, 0);
  useEffect(() => {
    const id = window.setInterval(tick, 60_000);
    return () => window.clearInterval(id);
  }, []);

  const stamp = new Date(refreshedAt);
  const age = formatAge(Date.now() - stamp.getTime());
  const exact = new Intl.DateTimeFormat('en-AU', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false,
  }).format(stamp);

  return (
    <div
      role="status"
      style={{
        background: C.amberLight, border: `1px solid ${C.amberBorder}`, borderRadius: 8,
        padding: '10px 14px', marginBottom: 12, fontFamily: F, fontSize: 13,
        color: C.amber, fontWeight: 700, lineHeight: 1.5,
      }}
    >
      ⚠ Offline — showing {scopeLabel} data from {age} ago (last refreshed {exact}).
      <span style={{ fontWeight: 400 }}> Bookings may have changed elsewhere since then.</span>
    </div>
  );
}

// Honest empty state for an offline view that has never been cached — explicitly NOT a
// spinner, and not the retryable ErrorBox (there is nothing to retry against).
export function OfflineEmptyState({ scopeLabel }: { scopeLabel: string }) {
  return (
    <div
      role="status"
      style={{
        background: C.surface, border: `1px dashed ${C.border}`, borderRadius: 10,
        padding: '36px 20px', textAlign: 'center', fontFamily: F,
      }}
    >
      <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 6 }}>
        No offline data for this view
      </div>
      <div style={{ fontSize: 13, color: C.textMuted, lineHeight: 1.5 }}>
        {scopeLabel} hasn’t been loaded on this device — connect to load it.
      </div>
    </div>
  );
}
